import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PW_KEY = "rss_admin_pw";

export const Admin = () => {
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alreadyIn, setAlreadyIn] = useState(
    typeof window !== "undefined" ? !!localStorage.getItem(ADMIN_PW_KEY) : false,
  );

  useEffect(() => {
    // Surface ?key= in the URL as a one-shot login (kept for convenience, not required)
    const url = new URL(window.location.href);
    const key = url.searchParams.get("key");
    if (key) {
      submit(key);
      url.searchParams.delete("key");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (raw) => {
    setError(null);
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: raw }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Invalid password");
      }
      localStorage.setItem(ADMIN_PW_KEY, raw);
      setAlreadyIn(true);
      // small delay so user sees feedback then drop them on home with the pill visible
      setTimeout(() => navigate("/", { replace: true }), 300);
    } catch (e) {
      setError(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    submit(pw.trim());
  };

  const signOut = () => {
    localStorage.removeItem(ADMIN_PW_KEY);
    setAlreadyIn(false);
  };

  return (
    <PageShell>
      <section className="max-w-md mx-auto px-6 py-16 sm:py-24">
        <div
          className="text-[10px] text-neutral-500 tracking-[0.4em] uppercase mb-3"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          Restricted
        </div>
        <h1
          className="text-white text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none"
          style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
        >
          <span className="text-[#FFC800]">Admin</span> Console
        </h1>
        <p className="mt-3 text-neutral-400 text-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
          Sign in to view the live-viewer counter.
        </p>

        <div className="mt-8 border border-neutral-800 bg-neutral-950/60 p-6">
          {alreadyIn ? (
            <div className="space-y-4" data-testid="admin-already-in">
              <div
                className="flex items-center gap-2 text-[#FFC800] text-xs tracking-[0.2em] uppercase"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                <Lock size={14} /> Signed in
              </div>
              <p className="text-neutral-300 text-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
                The live-viewer pill is now visible bottom-right on every page.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate("/")}
                  data-testid="admin-go-home-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#FFC800] hover:bg-[#FFC800]/80 text-black font-black py-3 text-xs tracking-[0.3em] uppercase"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Go Home <ArrowRight size={12} />
                </button>
                <button
                  onClick={signOut}
                  data-testid="admin-signout-btn"
                  className="border border-neutral-700 hover:border-white text-neutral-300 hover:text-white px-4 py-3 text-xs tracking-[0.3em] uppercase"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  Sign out
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
              <label
                className="block text-[10px] tracking-[0.3em] uppercase text-neutral-400"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                Password
              </label>
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="admin-password-input"
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-[#FFC800] text-white px-3 py-3 outline-none tracking-widest"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
                placeholder="••••"
              />
              {error && (
                <div
                  data-testid="admin-error"
                  className="text-[11px] text-red-400 tracking-wider"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !pw.trim()}
                data-testid="admin-submit-btn"
                className="w-full bg-[#FFC800] hover:bg-[#FFC800]/80 text-black font-black py-3 text-xs tracking-[0.3em] uppercase disabled:opacity-60"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {loading ? "Verifying..." : "Sign In"}
              </button>
            </form>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default Admin;
